import {Component, Injectable, OnInit, Optional} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {SubscriptionService} from '@services/subscription/subscription.service';
import {AccountService} from '@services/account/account.service';
import {Store} from '@ngrx/store';
import * as AuthReducer from '@store/auth/reducers';
import {MatDialog, MatDialogRef} from '@angular/material';
import {Subject} from 'rxjs';
import {ViewChild} from '@node_modules/@angular/core';
import {GooglePlaceDirective} from '@node_modules/ngx-google-places-autocomplete';
import {BehaviorSubject} from 'rxjs/internal/BehaviorSubject';
import {UpdateAuthUser} from '@store/auth/actions';
import {User} from '@models/user';
import {ActivatedRoute, Router} from '@node_modules/@angular/router';

@Component({
  selector: 'app-scenario-add',
  templateUrl: './scenario-add.component.html',
  styleUrls: ['./scenario-add.component.scss']
})
@Injectable()
export class ScenarioAddComponent implements OnInit {
  createSubForm: FormGroup;
  subscribes = [];
  user: User;
  errors = [];
  submitError = null;
  isSubmitting = false;
  readonly dropdownCtaValue = '__mkc_select_cta__';

  constructor(
    public dialog: MatDialog,
    @Optional() public dialogRef: MatDialogRef<ScenarioAddComponent>,
    private router: Router,
    private subscriptionService: SubscriptionService,
    private authStore: Store<AuthReducer.AuthState>
  ) { }

  ngOnInit() {
    // this.subscribes.push(this.authStore.subscribe(
    //   (val) => {
    //     if (val.auth && val.auth.user) {
    //       this.user = val.auth.user;
    //     }
    //   }
    // ));

    this.createSubForm = new FormGroup({
      'name': new FormControl(null, [Validators.required]),
      // MKC-78 S11 Iteration 1633 - canonical, mutually exclusive payer
      // mode. school_pays/student_pays are derived from this (see
      // isSchoolPayer()/isStudentPayer() below) purely so the rest of
      // this form's existing conditional sections/payload keys can stay
      // unchanged - the backend independently re-derives them from
      // payer_mode regardless of what is submitted here.
      'payer_mode': new FormControl(null, [Validators.required]),
      'per_school_amount': new FormControl(null),
      'per_school_frequency': new FormControl(this.dropdownCtaValue),
      'students_included_in_base_fee': new FormControl(0),
      'individual_bmu_offset': new FormControl(0),
      'group_bmu_offset': new FormControl(0),
      'per_student_amount': new FormControl(null),
      'per_student_frequency': new FormControl(this.dropdownCtaValue),
      'non_paying_individual_bmu_charge': new FormControl(null),
      'non_paying_group_bmu_charge': new FormControl(0),
      'individual_bmu_platform_fee': new FormControl(null),
      'group_bmu_platform_fee': new FormControl(0),
      'student_pays_amount': new FormControl(null),
      'student_pays_frequency': new FormControl(this.dropdownCtaValue),
    });
  }

  create() {
    if (this.isSubmitting) {
      return;
    }

    this.submitError = null;
    this.errors['name'] = null;
    this.errors['payer_mode'] = null;
    this.errors['per_school_amount'] = null;
    this.errors['students_included_in_base_fee'] = null;
    this.errors['individual_bmu_offset'] = null;
    this.errors['group_bmu_offset'] = null;
    this.errors['non_paying_individual_bmu_charge'] = null;
    this.errors['non_paying_group_bmu_charge'] = null;
    this.errors['individual_bmu_platform_fee'] = null;
    this.errors['group_bmu_platform_fee'] = null;

    if (this.createSubForm.get('name').value == null) {
      this.errors['name'] = 'This field is required';
      this.createSubForm.get('name').markAsTouched();
      return;
    }

    if (!this.isSchoolPayer() && !this.isStudentPayer()) {
      this.errors['payer_mode'] = 'Select who pays - School or Student.';
      this.createSubForm.get('payer_mode').markAsTouched();
      return;
    }

    if (this.isSchoolPayer()) {
      if (this.createSubForm.get('per_school_amount').value == null || this.createSubForm.get('per_school_amount').value === '') {
        this.errors['per_school_amount'] = 'This field is required';
        this.createSubForm.get('per_school_amount').markAsTouched();
        return;
      }
      if (this.createSubForm.get('students_included_in_base_fee').value == null || this.createSubForm.get('students_included_in_base_fee').value === '') {
        this.errors['students_included_in_base_fee'] = 'This field is required';
        this.createSubForm.get('students_included_in_base_fee').markAsTouched();
        return;
      }
      if (this.createSubForm.get('individual_bmu_offset').value == null || this.createSubForm.get('individual_bmu_offset').value === '') {
        this.errors['individual_bmu_offset'] = 'This field is required';
        this.createSubForm.get('individual_bmu_offset').markAsTouched();
        return;
      }
      if (this.createSubForm.get('group_bmu_offset').value == null || this.createSubForm.get('group_bmu_offset').value === '') {
        this.errors['group_bmu_offset'] = 'This field is required';
        this.createSubForm.get('group_bmu_offset').markAsTouched();
        return;
      }
      if (this.createSubForm.get('non_paying_individual_bmu_charge').value == null || this.createSubForm.get('non_paying_individual_bmu_charge').value === '') {
        this.errors['non_paying_individual_bmu_charge'] = 'This field is required';
        this.createSubForm.get('non_paying_individual_bmu_charge').markAsTouched();
        return;
      }
      if (this.createSubForm.get('non_paying_group_bmu_charge').value == null || this.createSubForm.get('non_paying_group_bmu_charge').value === '') {
        this.errors['non_paying_group_bmu_charge'] = 'This field is required';
        this.createSubForm.get('non_paying_group_bmu_charge').markAsTouched();
        return;
      }
    }

    if (this.isStudentPayer()) {
      if (this.createSubForm.get('individual_bmu_platform_fee').value == null || this.createSubForm.get('individual_bmu_platform_fee').value === '') {
        this.errors['individual_bmu_platform_fee'] = 'This field is required';
        this.createSubForm.get('individual_bmu_platform_fee').markAsTouched();
        return;
      }
      if (this.createSubForm.get('group_bmu_platform_fee').value == null || this.createSubForm.get('group_bmu_platform_fee').value === '') {
        this.errors['group_bmu_platform_fee'] = 'This field is required';
        this.createSubForm.get('group_bmu_platform_fee').markAsTouched();
        return;
      }
    }

    this.isSubmitting = true;
    this.subscriptionService.createScenario({
      name: this.createSubForm.get('name').value,
      payer_mode: this.createSubForm.get('payer_mode').value,
      school_pays: this.isSchoolPayer(),
      student_pays: this.isStudentPayer(),
      charged_to: this.compatibilityChargedTo(),
      per_school_amount: this.createSubForm.get('per_school_amount').value,
      per_school_frequency: this.isSchoolPayer() ? 'month' : null,
      students_included_in_base_fee: this.createSubForm.get('students_included_in_base_fee').value,
      individual_bmu_offset: this.createSubForm.get('individual_bmu_offset').value,
      group_bmu_offset: this.createSubForm.get('group_bmu_offset').value,
      non_paying_individual_bmu_charge: this.createSubForm.get('non_paying_individual_bmu_charge').value,
      non_paying_group_bmu_charge: this.createSubForm.get('non_paying_group_bmu_charge').value,
      individual_bmu_platform_fee: this.createSubForm.get('individual_bmu_platform_fee').value,
      group_bmu_platform_fee: this.createSubForm.get('group_bmu_platform_fee').value,
      per_student_amount: null,
      per_student_frequency: null,
      student_pays_amount: this.isStudentPayer() ? this.createSubForm.get('individual_bmu_platform_fee').value : null,
      student_pays_frequency: this.isStudentPayer() ? 'month' : null,
    }).subscribe(
      (resp: {data: {}}) => {
        this.isSubmitting = false;
        this.closeOrNavigate(true);
      },
      () => {
        this.isSubmitting = false;
        this.submitError = 'Unable to save the scenario right now. Please try again.';
      }
    );
  }

  genCancel(){
    this.closeOrNavigate();
  }

  isSchoolPayer(): boolean {
    return this.createSubForm.get('payer_mode').value === 'school';
  }

  isStudentPayer(): boolean {
    return this.createSubForm.get('payer_mode').value === 'student';
  }

  /**
   * MKC-78 S11 Iteration 1634 - the "Billing Method" dropdown (School
   * only / Student only / School and Student) was removed as a visible,
   * user-controlled selector (redundant with the canonical Who Pays
   * radio group). `charged_to` is still read internally by
   * SubscriptionScenario::getSchoolPlanSchoolPays() (school_pays &&
   * charged_to in [both, school]) for the read-only "School Plan"
   * summary text, so a safe compatibility value is still derived and
   * sent - the operator never chooses it, and "both" is never invented
   * as a new selection. A student-only Scenario never needs this value
   * (school_pays is false, so that summary method never reads it).
   */
  private compatibilityChargedTo(): string {
    return this.isSchoolPayer() ? 'school' : null;
  }

  private closeOrNavigate(result?: boolean) {
    if (this.dialogRef) {
      this.dialogRef.close(result);
      return;
    }

    this.router.navigate(['/subscriptions/scenario']);
  }

  private isDropdownCtaSelected(controlName: string): boolean {
    const value = this.createSubForm.get(controlName).value;
    return value === null || value === undefined || value === '' || value === this.dropdownCtaValue;
  }

  private dropdownPayloadValue(controlName: string) {
    return this.isDropdownCtaSelected(controlName) ? null : this.createSubForm.get(controlName).value;
  }

  liveBillingExampleLines(): string[] {
    const baseFee = this.createSubForm.get('per_school_amount').value;
    const allowance = this.createSubForm.get('students_included_in_base_fee').value;
    const nonPayingIndividual = this.createSubForm.get('non_paying_individual_bmu_charge').value;
    const nonPayingGroup = this.createSubForm.get('non_paying_group_bmu_charge').value;
    const individualOffset = this.createSubForm.get('individual_bmu_offset').value;
    const groupOffset = this.createSubForm.get('group_bmu_offset').value;
    const individualPlatformFee = this.createSubForm.get('individual_bmu_platform_fee').value;
    const groupPlatformFee = this.createSubForm.get('group_bmu_platform_fee').value;

    const lines: string[] = [];
    lines.push('Preview based on the current configuration.');
    lines.push('Base fee is charged monthly: ' + this.formatMoneyInput(baseFee));
    lines.push('Students included in base fee: ' + this.formatAllowanceInput(allowance));
    lines.push('Growth charges begin after the allowance is exceeded.');
    lines.push('Non-paying BMU charges: individual ' + this.formatMoneyInput(nonPayingIndividual) + ', group ' + this.formatMoneyInput(nonPayingGroup));
    lines.push('Every paying BMU earns an offset immediately: individual ' + this.formatMoneyInput(individualOffset) + ', group ' + this.formatMoneyInput(groupOffset));
    lines.push('Student platform fees stay separate from offsets: individual ' + this.formatMoneyInput(individualPlatformFee) + ', group ' + this.formatMoneyInput(groupPlatformFee));
    lines.push('Billing cadence is monthly only.');

    return lines;
  }

  offsetPlaceholderText(): string {
    return 'Individual BMU Offset and Group BMU Offset are now editable commercial values.';
  }

  advancedFields(): string[] {
    return [
      'Charged on basis of',
      'Base fee amount',
      'Base fee frequency',
      'Students included in base fee',
      'Non-paying individual BMU charge',
      'Non-paying individual BMU charge frequency',
      'Non-paying group BMU charge',
      'Individual BMU offset',
      'Group BMU offset',
      'Individual BMU platform fee',
      'Individual BMU platform fee frequency',
      'Group BMU platform fee',
    ];
  }

  private whoPaysSummary(schoolPays, studentPays): string {
    if (schoolPays == 1 && studentPays == 1) {
      return 'School and Student';
    }

    if (schoolPays == 1) {
      return 'School';
    }

    if (studentPays == 1) {
      return 'Student';
    }

    return 'Not set';
  }

  private formatAllowanceInput(value): string {
    if (value === null || value === undefined || value === '') {
      return '0 students';
    }

    return String(value) + ' students';
  }

  private formatMoneyInput(value): string {
    if (value === null || value === undefined || value === '') {
      return 'not set';
    }

    return String(value);
  }
}
